import { Plus } from 'lucide-react';
import { MediaManagerBoard, MediaManagerTemplate } from 'modules/core/domain/interfaces/supabase/media-manager/entity';
import supabase from 'modules/core/supabase/supabase';
import MediaManagerBoardList from 'modules/manager/ui/components/media-manager-board-list';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import UDButton from 'ud-ui/components/button';
import { useMainLayout } from 'ud-ui/layout/main/context';
import useCreateBoardModal from '../hooks/modals/use-create-board-modal';
import useDeleteBoardModal from '../hooks/modals/use-delete-board-modal';
import useEditBoardModal from '../hooks/modals/use-edit-board-modal';

const MediaManagerBoardsListPage: React.FC = () => {
  const { setHeaderContent } = useMainLayout();
  const navigate = useNavigate();
  const { templateId: templateIdParam } = useParams<{ templateId: string }>();
  const templateId = Number(templateIdParam);

  const openBoard = useCallback(
    (board: MediaManagerBoard) => {
      navigate(Pages.MEDIA_MANAGER.builders.editor(templateId, board.id));
    },
    [navigate, templateId],
  );

  const [template, setTemplate] = useState<MediaManagerTemplate | null>(null);
  const [boards, setBoards] = useState<MediaManagerBoard[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const isTemplateLoading = isLoading && !template;
  const headerTitle = template?.name ?? 'Табло шаблона';

  const breadcrumb = useMemo(
    () => (
      <div className="flex flex-row items-center">
        <UDBreadcrumbs
          breadcrumbs={[
            { label: 'Медиа менеджер', to: Pages.MEDIA_MANAGER.INDEX },
            ...(isTemplateLoading ? [] : [{ label: headerTitle, to: Pages.MEDIA_MANAGER.builders.boards(templateId) }]),
          ]}
        />
        {isTemplateLoading && <span className="skeleton-block ml-2 inline-block h-5 w-40" />}
      </div>
    ),
    [headerTitle, isTemplateLoading, templateId],
  );

  useEffect(() => {
    setHeaderContent(breadcrumb);
    return () => setHeaderContent(null);
  }, [setHeaderContent, breadcrumb]);

  const loadBoards = useCallback(() => {
    if (!templateId || Number.isNaN(templateId)) return;
    setIsLoading(true);
    supabase.mediaManager
      .getTemplateWithBoards(templateId)
      .then(({ template: nextTemplate, boards: nextBoards }) => {
        setTemplate(nextTemplate);
        setBoards(nextBoards);
      })
      .catch((error) => {
        console.error('Failed to load media manager boards:', error);
      })
      .finally(() => setIsLoading(false));
  }, [templateId]);

  useEffect(() => {
    loadBoards();
  }, [loadBoards]);

  const existingNames = useMemo(() => boards.map((b) => b.name.trim()), [boards]);
  const { modal: createModal, open: openCreateModal } = useCreateBoardModal({
    templateId,
    existingNames,
    boardsCount: boards.length,
    onCreated: loadBoards,
  });
  const { modal: editModal, open: openEditModal } = useEditBoardModal({
    boards,
    onUpdated: loadBoards,
  });
  const { modal: deleteModal, open: openDeleteModal } = useDeleteBoardModal({
    onDeleted: loadBoards,
  });

  return (
    <div className="flex h-full flex-col p-6">
      <div className="mb-4 flex justify-start">
        <UDButton variant="dark" className="flex items-center" onClick={openCreateModal}>
          <Plus size={16} className="mr-2" />
          Добавить табло
        </UDButton>
      </div>

      <MediaManagerBoardList
        boards={boards}
        isLoading={isLoading}
        onCreate={openCreateModal}
        onSelect={openBoard}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      {createModal}
      {editModal}
      {deleteModal}
    </div>
  );
};

export default MediaManagerBoardsListPage;
